<template>
    <header class="relative top-0 z-30">
        <div id="navigation" class="overflow-x-hidden">
            <UiHeaderUSPs />
            <div
                :class="[
                    {
                        '!bg-transparent': enabledSearchPopup,
                        'max-xl:!bg-dark-900 max-xl:text-white': headerBarShopTheme === 'men',
                        'xl:!bg-dark-900 xl:text-white': !hasMenuStyling && headerBarShopTheme === 'men',
                        'xl:!bg-opacity-8 xl:bg-white xl:backdrop-blur-lg':
                            !hasMenuStyling && headerBarShopTheme === 'ladies',
                    },
                    menuStylingClasses,
                ]"
                class="menu-styling-bar py-2 backdrop-filter-none transition-all xl:backdrop-blur-sm">
                <div class="normal-container flex items-center justify-between">
                    <UiHeaderMainNavigation
                        v-model:searchQuery="searchQuery"
                        :navigationElements
                        :data
                        :isOpen
                        :selectedNavigationElement
                        :navigationElementsChildren
                        @showChildren="showChildren"
                        @closeMobileNavigation="closeMobileNavigation"
                        @openMobileNavigation="isOpen = true"
                        @showSearchPopup="enableSearchPopup"
                        @closeSearchPopup="closeSearchPopup" />
                    <UiHeaderLogo @click="closeSearchPopup" :data />
                    <div class="flex w-full justify-end">
                        <div class="hidden items-center xl:flex">
                            <div v-if="enabledSearchPopup" class="flex items-center">
                                <input
                                    v-model="searchQuery"
                                    id="search-input"
                                    role="searchbox"
                                    class="h-9 w-[250px] translate-x-7 bg-white px-5 text-sm !text-black focus-visible:outline-0"
                                    type="text"
                                    :aria-description="$snippets['headless.header.search']"
                                    :placeholder="$snippets['headless.header.search']"
                                    @keyup.enter="handleEnter" />
                                <MagnifyingGlassIcon class="z-50 size-5 stroke-2 lg:size-6" />
                            </div>
                            <button v-if="!enabledSearchPopup" @click="enableSearchPopup">
                                <MagnifyingGlassIcon
                                    class="size-5 stroke-2 duration-300 hover:text-green-500 lg:size-6"
                                    :aria-label="$snippets['headless.header.openSearch'] ?? 'Open zoeken'" />
                            </button>
                        </div>
                        <UiUserAccountButton shouldShowName class="hidden xl:flex" />
                        <UiHeaderWishlist />
                        <UiHeaderCartLink />
                    </div>
                </div>
            </div>
            <!-- Mobile search input -->
            <div class="border-y border-green-400 bg-white py-3 xl:hidden" @click="enableSearchPopup()">
                <div class="normal-container flex items-center">
                    <MagnifyingGlassIcon class="mr-5 size-6 stroke-2" />
                    <!-- Mobile non popup -->
                    <input
                        v-model="searchQuery"
                        class="w-full text-xs leading-none tracking-[0.6px] placeholder:text-black focus:!outline-none focus-visible:outline-0"
                        type="text"
                        role="searchbox"
                        :aria-description="$snippets['headless.header.lookingFor']"
                        :placeholder="$snippets['headless.header.lookingFor']"
                        @keyup.enter="handleEnter" />
                </div>
            </div>
        </div>

        <UiHeaderChildNavigation :navigationElements :data />

        <transition name="fade">
            <div
                v-if="enabledSearchPopup"
                class="fixed inset-0 z-50 flex flex-col bg-white xl:relative xl:z-auto xl:block xl:bg-transparent">
                <div class="shrink-0 bg-white xl:relative xl:z-auto">
                    <div class="border-y py-4 xl:hidden">
                        <div class="full-width max:!mx-auto !mx-[20px] flex items-center justify-between lg:!mx-10">
                            <div class="flex flex-1 items-center">
                                <MagnifyingGlassIcon class="mr-5 size-6 stroke-2" />
                                <input
                                    v-model="searchQuery"
                                    id="search-input-mobile"
                                    class="w-full text-xs leading-none tracking-[0.6px] placeholder:text-black focus:!outline-none focus-visible:outline-0"
                                    type="text"
                                    role="searchbox"
                                    :aria-description="$snippets['headless.header.lookingFor']"
                                    :placeholder="$snippets['headless.header.lookingFor']"
                                    @keyup.enter="handleEnter" />
                            </div>
                            <button @click="handleXMarkClick" class="ml-4">
                                <XMarkIcon class="size-6 stroke-2" />
                            </button>
                        </div>
                    </div>
                </div>
                <TweakwiseSeachPopup
                    v-if="enabledSearchPopup"
                    @closeSearchPopup="closeSearchPopup"
                    @closeSearchPopupWithNavigation="closeSearchPopupWithNavigation"
                    :searchQuery="searchQuery"
                    class="min-h-0 flex-1 overflow-y-auto overscroll-contain xl:overflow-visible" />
            </div>
        </transition>
    </header>
</template>

<script setup lang="ts">
    import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline';
    import { storeToRefs } from 'pinia';

    const route = useRoute();
    const isOpen = ref(false);
    const searchQuery = ref('');
    const { width } = useWindowSize();
    const enabledSearchPopup = ref(false);
    const selectedNavigationElement = ref('');
    const pageDataStore = usePageDataStore();
    const { data } = storeToRefs(pageDataStore);

    /** URL first so SSR matches hydration; ignore stale Pinia on locale home (see UiHeaderLogo). */
    const headerBarShopTheme = computed<'men' | 'ladies' | null>(() => {
        const path = route.path;
        if (path.includes('/heren')) {
            return 'men';
        }
        if (path.includes('/dames')) {
            return 'ladies';
        }
        if (/^\/[a-z]{2}\/?$/i.test(path)) {
            return null;
        }
        const ctx = data.value?.context;
        if (ctx === 'men') {
            return 'men';
        }
        if (ctx === 'ladies') {
            return 'ladies';
        }
        return null;
    });

    /** Per-page menu styling from Sanity (page.menuStyling). Empty = default styling above.
     * Desktop (xl) only — mobile always keeps the default styling. */
    const menuStyling = useMenuStyling();
    const hasMenuStyling = computed(() =>
        Boolean(menuStyling.value?.backgroundColor || menuStyling.value?.textColor),
    );

    const menuBackgroundClasses: Record<string, string> = {
        black: 'xl:!bg-black',
        transparent: 'xl:!bg-transparent',
        white: 'xl:!bg-white',
        green: 'xl:!bg-green-600',
    };
    const menuTextClasses: Record<string, string> = {
        black: 'xl:!text-black',
        white: 'xl:!text-white',
        green: 'xl:!text-green-600',
    };

    const menuStylingClasses = computed(() => {
        const classes: string[] = [];
        // The search popup forces a transparent bar; don't fight it with another !bg-* class
        if (menuStyling.value?.backgroundColor && !enabledSearchPopup.value) {
            classes.push(menuBackgroundClasses[menuStyling.value.backgroundColor] ?? '');
        }
        if (menuStyling.value?.textColor) {
            // menu-styling-text makes .navigation-item links inherit this color (see main.css)
            classes.push(menuTextClasses[menuStyling.value.textColor] ?? '', 'menu-styling-text');
        }
        return classes;
    });

    // SSR renders this header BEFORE the page's Sanity fetch resolves (the Pagebuilder sets
    // the menuStyling state from inside the page slot), so the class bindings above ship the
    // default styling and only correct themselves at hydration — which nuxt-delay-hydration
    // postpones on production, making the configured color appear seconds late. Head attributes
    // are resolved after the whole app has rendered, so mirroring the styling onto <html> gets
    // the right colors into the SSR HTML; main.css paints .menu-styling-bar from these attrs.
    useHead({
        htmlAttrs: computed(() => ({
            // The search popup forces a transparent bar; drop the attr so the CSS doesn't fight it
            'data-menu-bg': enabledSearchPopup.value ? undefined : menuStyling.value?.backgroundColor,
            'data-menu-text': menuStyling.value?.textColor,
        })),
    });

    const navigationElementsChildren = ref([]);
    const { loadNavigationElements, navigationElements } = useNavigation();
    // Don't let a failed navigation fetch crash the page (error.vue also SSRs this header)
    await loadNavigationElements({ depth: 3 }).catch(() => {});

    function enableSearchPopup() {
        enabledSearchPopup.value = true;
        // Mobile menu is z-[70] and would cover the z-50 popup entirely
        isOpen.value = false;

        // Wait for DOM updates
        if (process.client) {
            nextTick(() => {
                const isMobile = window.innerWidth < 1280;
                const selector = isMobile ? '#search-input-mobile' : '#search-input';
                const input = document.querySelector(selector) as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            });
        }
    }

    function handleEnter() {
        if (searchQuery.value.trim()) {
            navigateTo(`/search?tn_q=${encodeURIComponent(searchQuery.value)}`);
            enabledSearchPopup.value = false;
            isOpen.value = false;
        }
    }

    function showChildren(navigationElement: object) {
        selectedNavigationElement.value = navigationElement?.translated?.name;
        navigationElementsChildren.value = navigationElement.children;
    }

    onMounted(async () => {
        window.addEventListener('click', function (e) {
            if (!isOpen.value) {
                return;
            }
            const searchPopupElement = document.getElementById('searchPopup');
            const mobileNavigationElement = document.getElementById('mobile-navigation');
            const openToggleButton = document.getElementById('open-mobile-navigation');

            const clickedOutsideMobileNav =
                mobileNavigationElement && !mobileNavigationElement.contains(e.target as Node);
            const clickedOutsideSearchPopup =
                !searchPopupElement || (searchPopupElement && !searchPopupElement.contains(e.target as Node));
            const clickedOpenToggle = openToggleButton && openToggleButton.contains(e.target as Node);

            if (clickedOutsideMobileNav && clickedOutsideSearchPopup && !clickedOpenToggle) {
                isOpen.value = false;
            }
        });

        if (data.value?.context === 'ladies') {
            showChildren(navigationElements.value[0]);
        } else if (data.value?.context === 'men') {
            showChildren(navigationElements.value[1]);
        } else {
            showChildren(navigationElements.value[0]);
        }
    });

    watch(
        () => pageDataStore.data,
        newData => {
            if (newData?.context === 'ladies') {
                showChildren(navigationElements.value[0]);
            } else if (newData?.context === 'men') {
                showChildren(navigationElements.value[1]);
            }
        },
        { immediate: true },
    );

    watch(isOpen, value => {
        if (process.client) {
            if (value && width.value < 1280) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    });

    // Add watch for search popup
    watch(enabledSearchPopup, value => {
        if (process.client) {
            if (value) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    });

    // Watch the routepath for changes, if it changes close the search popup
    watch(
        () => route.path,
        () => {
            enabledSearchPopup.value = false; // Disable search popup on route change
        },
    );

    function closeMobileNavigation() {
        isOpen.value = false;
    }

    function closeSearchPopup() {
        enabledSearchPopup.value = false;
        // searchQuery.value = '';
    }

    function closeSearchPopupWithNavigation() {
        enabledSearchPopup.value = false;
        isOpen.value = false;
        // searchQuery.value = '';
    }

    function handleXMarkClick() {
        if (searchQuery.value.trim()) {
            const isMobile = window.innerWidth < 1280;
            const selector = isMobile ? '#search-input-mobile' : '#search-input';
            const input = document.querySelector(selector) as HTMLInputElement;
            if (input) {
                input.focus();
            }
            searchQuery.value = '';
        } else {
            closeSearchPopup();
        }
    }
</script>

<style scoped>
    @reference "tailwindcss";

    .group:hover .img-hover-filter {
        filter: invert(97%) sepia(6%) saturate(392%) hue-rotate(20deg) brightness(90%) contrast(88%);
    }

    body,
    p {
        @apply text-sm leading-none;
    }
</style>
